import type {
    InventoryAggregatedMetricKey,
    InventoryMetricAggregates,
    InventoryMetricChange,
    InventoryMetricsChange,
    InventoryMetricsSummaryMetrics,
    InventoryMetricsSummaryResponse,
    InventoryReportingTimeseriesDataPoint,
    InventorySampleType,
} from '../../../types/reporting';
import type { WidgetStat } from './types';

/** UI metric keys that are derived client-side (not returned by the API). */
export const DERIVED_INVENTORY_METRICS = new Set([
    'netQuantityChange',
    'netTransactionValue',
]);

/**
 * Catalog / widget metric → API aggregate field.
 * Avg/Median variants use avg* / median* prefixes that map to the same base field.
 */
const AVG_PREFIX = 'avg';
const MEDIAN_PREFIX = 'median';

/** PascalCase → camelCase field map for avg/median catalog values. */
const VARIANT_BASE_FIELDS: Record<string, InventoryAggregatedMetricKey> = {
    InStockValue: 'inStockValue',
    InStockQuantity: 'inStockQuantity',
    ZeroCostStockQuantity: 'zeroCostStockQuantity',
    OrderedQuantity: 'orderedQuantity',
    OrderedValue: 'orderedValue',
    InitialQuantity: 'initialQuantity',
    InitialValue: 'initialValue',
    UsedQuantity: 'usedQuantity',
    UsedValue: 'usedValue',
    SoldQuantity: 'soldQuantity',
    SalesValue: 'salesValue',
    InboundQuantity: 'inboundQuantity',
    InboundValue: 'inboundValue',
    OutboundQuantity: 'outboundQuantity',
    OutboundValue: 'outboundValue',
};

export function parseInventoryMetricKey(metric: string): {
    field: InventoryAggregatedMetricKey | 'netQuantityChange' | 'netTransactionValue' | null;
    stat: WidgetStat;
} {
    if (metric === 'netQuantityChange' || metric === 'netTransactionValue') {
        return { field: metric, stat: 'total' };
    }
    if (metric.startsWith(AVG_PREFIX)) {
        const base = VARIANT_BASE_FIELDS[metric.slice(AVG_PREFIX.length)];
        return { field: base ?? null, stat: 'average' };
    }
    if (metric.startsWith(MEDIAN_PREFIX)) {
        const base = VARIANT_BASE_FIELDS[metric.slice(MEDIAN_PREFIX.length)];
        return { field: base ?? null, stat: 'median' };
    }
    // Direct base key (total variant)
    if ((Object.values(VARIANT_BASE_FIELDS) as string[]).includes(metric)) {
        return { field: metric as InventoryAggregatedMetricKey, stat: 'total' };
    }
    return { field: null, stat: 'total' };
}

export function inventoryMetricNeedsSampleType(metric: string): boolean {
    const { stat } = parseInventoryMetricKey(metric);
    return stat === 'average' || stat === 'median';
}

type InventoryMetricSource =
    | InventoryMetricsSummaryMetrics
    | InventoryReportingTimeseriesDataPoint
    | NonNullable<InventoryReportingTimeseriesDataPoint['prior']>
    | null
    | undefined;

function readAggregates(
    source: InventoryMetricSource,
    field: InventoryAggregatedMetricKey,
): InventoryMetricAggregates | undefined {
    if (!source) return undefined;
    const value = (source as Record<string, unknown>)[field];
    if (!value || typeof value !== 'object') return undefined;
    return value as InventoryMetricAggregates;
}

function pickAggregateValue(
    aggregates: InventoryMetricAggregates | undefined,
    stat: WidgetStat,
    sampleType: InventorySampleType,
): number | undefined {
    if (!aggregates) return undefined;
    if (stat === 'total') return aggregates.total;
    if (stat === 'average') return aggregates.average[sampleType];
    return aggregates.median[sampleType];
}

function deriveNet(
    source: InventoryMetricSource,
    kind: 'quantity' | 'value',
): number | undefined {
    if (!source) return undefined;
    if (kind === 'quantity') {
        const inbound = source.inboundQuantity?.total;
        const outbound = source.outboundQuantity?.total;
        if (typeof inbound !== 'number' || typeof outbound !== 'number') return undefined;
        return inbound - outbound;
    }
    const inbound = source.inboundValue?.total;
    const outbound = source.outboundValue?.total;
    if (typeof inbound !== 'number' || typeof outbound !== 'number') return undefined;
    return inbound - outbound;
}

export function resolveInventoryMetricValue(
    source: InventoryMetricSource,
    metric: string,
    sampleType: InventorySampleType = 'perUnit',
): number | undefined {
    const { field, stat } = parseInventoryMetricKey(metric);
    if (!field) return undefined;
    if (field === 'netQuantityChange') return deriveNet(source, 'quantity');
    if (field === 'netTransactionValue') return deriveNet(source, 'value');
    return pickAggregateValue(readAggregates(source, field), stat, sampleType);
}

function pickChangeLeaf(
    change: InventoryMetricChange | undefined,
    stat: WidgetStat,
    sampleType: InventorySampleType,
): number | undefined {
    if (!change) return undefined;
    if (stat === 'total') return change.total;
    if (stat === 'average') return change.average?.[sampleType];
    return change.median?.[sampleType];
}

export function resolveInventoryMetricChange(
    summary: InventoryMetricsSummaryResponse | undefined,
    metric: string,
    sampleType: InventorySampleType = 'perUnit',
): number | undefined {
    if (!summary?.change) return undefined;
    const { field, stat } = parseInventoryMetricKey(metric);
    if (!field) return undefined;

    // Derived nets: compute % change from current/prior derived totals when possible.
    if (field === 'netQuantityChange' || field === 'netTransactionValue') {
        const current = resolveInventoryMetricValue(summary, metric, sampleType);
        const prior = resolveInventoryMetricValue(summary.prior, metric, sampleType);
        if (current === undefined || prior === undefined || prior === 0) return undefined;
        return Number((((current / prior) - 1) * 100).toFixed(2));
    }

    const changeMap = summary.change as InventoryMetricsChange;
    return pickChangeLeaf(changeMap[field], stat, sampleType);
}

/** Segment / flow helpers — always use `.total` for stacked quantity-flow charts. */
export function aggregateTotal(
    aggregates: InventoryMetricAggregates | number | null | undefined,
): number {
    if (typeof aggregates === 'number') return aggregates;
    if (!aggregates) return 0;
    return aggregates.total ?? 0;
}
